import React from 'react';
import { Icon, IconName } from '../../DesignSystem';
import './ProfileSuggestions.css';

interface ProfileSuggestionsProps {
  missingFields: string[];
  onFieldClick?: (field: string) => void;
  maxSuggestions?: number;
  className?: string;
}

// Map field names to user-friendly labels and icons
const FIELD_CONFIG: Record<string, { label: string; icon: IconName }> = {
  organizer: { label: 'Add organization name', icon: 'Building2' },
  subtitle: { label: 'Add tagline', icon: 'Quote' },
  description: { label: 'Add description', icon: 'FileText' },
  location: { label: 'Add location', icon: 'MapPin' },
  organizerImage: { label: 'Add profile photo', icon: 'Camera' },
  galleryImages: { label: 'Add gallery images', icon: 'Images' },
  contactInfo: { label: 'Add contact info', icon: 'AtSign' }
};

/**
 * Displays suggestions for missing profile fields
 * Clickable items that can trigger the edit flow
 */
const ProfileSuggestions: React.FC<ProfileSuggestionsProps> = ({
  missingFields,
  onFieldClick,
  maxSuggestions = 3,
  className = ''
}) => {
  if (missingFields.length === 0) {
    return null;
  }

  // Filter and limit suggestions
  const visibleFields = missingFields
    .filter(field => FIELD_CONFIG[field])
    .slice(0, maxSuggestions);

  const remainingCount = missingFields.length - visibleFields.length;

  if (visibleFields.length === 0) {
    return null;
  }

  return (
    <div className={`profile-suggestions ${className}`}>
      <div className="profile-suggestions__header">
        <Icon name="Lightbulb" size={14} />
        <span>Complete your profile</span>
      </div>
      <div className="profile-suggestions__list">
        {visibleFields.map(field => {
          const config = FIELD_CONFIG[field];
          return (
            <button
              key={field}
              className="profile-suggestions__item"
              onClick={() => onFieldClick?.(field)}
              type="button"
            >
              <Icon name={config.icon} size={14} />
              <span>{config.label}</span>
            </button>
          );
        })}
        {remainingCount > 0 && (
          <button
            className="profile-suggestions__item profile-suggestions__item--more"
            onClick={() => onFieldClick?.('all')}
            type="button"
          >
            <Icon name="Plus" size={14} />
            <span>{remainingCount} more fields</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfileSuggestions;
